import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Sparkle, ArrowRight, Envelope, Lock, User } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function AuthPage({ mode = "login" }) {
  const { login, register } = useAuth();
  const { lang } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isLogin = mode === "login";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isLogin) await login(email.trim(), password);
      else await register(email.trim(), password, name.trim());
      toast.success(lang === "el" ? "Καλώς ήρθες!" : "Welcome!");
      nav(loc.state?.from || "/", { replace: true });
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-6 py-12" data-testid="auth-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 kicker-label mb-4">
            <Sparkle size={14} weight="duotone" className="text-forest" />
            {isLogin ? (lang === "el" ? "Σύνδεση" : "Sign in") : (lang === "el" ? "Νέος λογαριασμός" : "Sign up")}
          </div>
          <h1 className="font-heading font-semibold text-[36px] md:text-[44px] leading-[0.95] tracking-tight text-ink">
            {isLogin
              ? (lang === "el" ? "Καλώς όρισες πίσω." : "Welcome back.")
              : (lang === "el" ? "Ας ξεκινήσουμε." : "Let's begin.")}
          </h1>
          <p className="mt-3 text-ink-soft text-sm">
            {isLogin
              ? (lang === "el" ? "Συνέχισε με το email σου." : "Continue with your email.")
              : (lang === "el" ? "Δημιούργησε λογαριασμό σε 30 δευτερόλεπτα." : "Create an account in 30 seconds.")}
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4 bg-white border border-line rounded-2xl canvas-shadow p-6" data-testid="auth-form">
          {!isLogin && (
            <div>
              <label className="kicker-label mb-2 block">{lang === "el" ? "Όνομα" : "Name"}</label>
              <div className="relative">
                <User size={16} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-soft" />
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder={lang === "el" ? "Το όνομά σου" : "Your name"}
                  className="cs-input !pl-11"
                  data-testid="auth-name"
                />
              </div>
            </div>
          )}
          <div>
            <label className="kicker-label mb-2 block">Email</label>
            <div className="relative">
              <Envelope size={16} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-soft" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="cs-input !pl-11"
                data-testid="auth-email"
              />
            </div>
          </div>
          <div>
            <label className="kicker-label mb-2 block">{lang === "el" ? "Κωδικός" : "Password"}</label>
            <div className="relative">
              <Lock size={16} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-soft" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                placeholder="••••••••"
                className="cs-input !pl-11"
                data-testid="auth-password"
              />
            </div>
          </div>

          {error && (
            <div className="text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-lg px-3 py-2" data-testid="auth-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-forest text-cream rounded-full px-6 py-3.5 text-sm font-medium hover:bg-forest-dark transition disabled:opacity-60"
            data-testid="auth-submit"
          >
            {loading ? "…" : (isLogin ? (lang === "el" ? "Σύνδεση" : "Sign in") : (lang === "el" ? "Δημιουργία λογαριασμού" : "Create account"))}
            <ArrowRight size={14} weight="bold" />
          </button>

          <div className="text-center text-sm text-ink-soft pt-2">
            {isLogin ? (
              <>
                {lang === "el" ? "Δεν έχεις λογαριασμό;" : "No account yet?"}{" "}
                <Link to="/register" className="text-forest hover:text-forest-dark font-medium" data-testid="link-to-register">
                  {lang === "el" ? "Δημιούργησε έναν" : "Create one"}
                </Link>
              </>
            ) : (
              <>
                {lang === "el" ? "Έχεις ήδη λογαριασμό;" : "Already have an account?"}{" "}
                <Link to="/login" className="text-forest hover:text-forest-dark font-medium" data-testid="link-to-login">
                  {lang === "el" ? "Σύνδεσου" : "Sign in"}
                </Link>
              </>
            )}
          </div>
          {isLogin && (
            <div className="text-center text-xs text-ink-soft/80 pt-1">
              <Link to="/forgot-password" className="hover:text-forest underline underline-offset-2" data-testid="forgot-password-link">
                {lang === "el" ? "Ξέχασες τον κωδικό σου;" : "Forgot your password?"}
              </Link>
            </div>
          )}
        </form>

        <div className="text-center mt-6 text-[11px] text-ink-soft uppercase tracking-[0.14em]">
          {lang === "el" ? "Δωρεάν beta πρόσβαση" : "Free beta access"}
        </div>
      </div>
    </div>
  );
}
